import { posthog } from 'posthog-js';
import React, { createContext, useContext, useMemo } from 'react';

import {
  type FeatureFlagConfig,
  type FeatureFlagProperties,
  type FeatureFlagProperty,
  type IFeatureFlagService,
} from '@/services/FeatureFlag/featureFlags';

import getEnvironment from '../config/environment';

export class PostHogFeatureFlagService implements IFeatureFlagService {
  private static instance: PostHogFeatureFlagService;

  init(config: FeatureFlagConfig): void {
    posthog.init(config.apiKey, {
      ...config.options,
      autocapture: false,
      capture_pageview: false,
      disable_session_recording: true,
      loaded: (posthog) => {
        if (getEnvironment().envName == 'local') {
          console.debug('Posthog initialized with debug mode:On');
          posthog.debug();
        }
      },
    });
  }

  static getInstance(): PostHogFeatureFlagService {
    if (!PostHogFeatureFlagService.instance) {
      PostHogFeatureFlagService.instance = new PostHogFeatureFlagService();
    }
    return PostHogFeatureFlagService.instance;
  }

  identify(userId: string, properties?: Record<string, FeatureFlagProperty>): void {
    posthog.identify(userId, properties);
  }

  sessionProperties(properties: FeatureFlagProperties): void {
    posthog.setPersonProperties(properties);
  }

  group(
    groupType: string,
    groupKey: string,
    properties?: Record<string, FeatureFlagProperty>,
  ): void {
    posthog.group(groupType, groupKey, properties);
  }

  isFeatureEnabled(flag: string): boolean {
    return Boolean(posthog.isFeatureEnabled(flag));
  }

  hasLoadedFlags(): boolean {
    return posthog.featureFlags.hasLoadedFlags;
  }

  getFeatureFlag(flag: string): boolean | string | undefined {
    return posthog.getFeatureFlag(flag);
  }

  onFeatureFlags(callback: (flags: string[]) => void): () => void {
    return posthog.onFeatureFlags(callback);
  }

  reset(): void {
    posthog.reset();
  }
}

export const FeatureFlagContext = createContext<IFeatureFlagService | null>(null);

interface FeatureFlagProviderProps {
  children: React.ReactNode;
  config: FeatureFlagConfig;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children, config }) => {
  const service = useMemo(() => {
    const newService = PostHogFeatureFlagService.getInstance();
    newService.init(config);
    return newService;
  }, [config]);

  return <FeatureFlagContext.Provider value={service}>{children}</FeatureFlagContext.Provider>;
};

export const useFeatureFlags = (): IFeatureFlagService => {
  const service = useContext(FeatureFlagContext);
  if (!service) {
    throw new Error('useFeatureFlagService must be used within a FeatureFlagProvider');
  }
  return service;
};
